import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useUserHook from "../../../Hooks/userHook";
import useHttpHook from "../../../Hooks/HttpHook";
import {
  GetFollowers,
  GetUserEventsList,
  OrganizerEventApi,
  getOrganizerEventsByUsername,
} from "../../../utils/ApiConstant";
import { getImageUrl } from "../../../utils/Helper";
import {
  DEFAULT_BANNER_IMAGE,
  DEFAULT_PROFILE_IMAGE,
} from "../../../utils/brandAssets";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../utils/notify";

const EventV2ProfileCard = ({ onNavigate, viewedUserName, profileUser }) => {
  const [, sendRequest, error] = useHttpHook();
  const { getProfilePic, user } = useUserHook();
  const { userDetails, masterToken } = useSelector((state) => state.user);
  const [followers, setFollowersCount] = useState(0);
  const [following, setFollowingCount] = useState(0);
  const [eventsCount, setEventsCount] = useState(0);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);

  const viewingOtherProfile =
    Boolean(viewedUserName) && viewedUserName !== user?.userName;
  // Never reuse another user's profile data on hub pages (e.g. after Event list).
  const displayUser = viewingOtherProfile
    ? profileUser
    : user || userDetails;
  const profileUserName = viewingOtherProfile
    ? viewedUserName
    : displayUser?.userName || user?.userName;

  const getFollowers = useCallback(async () => {
    if (!profileUserName) return;

    try {
      const res = await sendRequest(`${GetFollowers}/${profileUserName}`, {
        method: "get",
      });
      if (res) {
        setFollowersCount(res.followers ?? res.follower ?? 0);
        setFollowingCount(res.following ?? 0);
      }
    } catch (err) {
      console.log({ err });
    }
  }, [profileUserName, sendRequest]);

  const getEventsCount = useCallback(async () => {
    if (!profileUserName) return;

    try {
      let res;
      if (viewingOtherProfile) {
        res = await sendRequest(
          `${getOrganizerEventsByUsername}/${profileUserName}?page=1`,
          { method: "get" }
        );
      } else if (userDetails?.userType === "organizer") {
        res = await sendRequest(`${OrganizerEventApi}?page=1`, {
          method: "get",
        });
      } else {
        res = await sendRequest(`${GetUserEventsList}?page=1`, {
          method: "get",
        });
      }

      if (typeof res?.data?.paginator?.itemCount === "number") {
        setEventsCount(res.data.paginator.itemCount);
      }
    } catch (err) {
      console.log({ err });
    }
  }, [
    viewingOtherProfile,
    profileUserName,
    sendRequest,
    userDetails?.userType,
  ]);

  useEffect(() => {
    if (profileUserName) {
      getFollowers();
      getEventsCount();
    }
  }, [profileUserName, getFollowers, getEventsCount]);

  useEffect(() => {
    setAvatarFailed(false);
    setCoverFailed(false);
  }, [displayUser?.profilePic, displayUser?.coverPic, profileUserName]);

  useEffect(() => {
    if (error && toast.isActive(error) === false) {
      showErrorToast(error);
      if (error === "You are unAuthrised") {
        window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
      }
    }
  }, [error]);

  if (!displayUser && !profileUserName) return null;

  const coverFallback = DEFAULT_BANNER_IMAGE;
  const avatarFallback = DEFAULT_PROFILE_IMAGE;

  const rawCover = viewingOtherProfile
    ? displayUser?.coverPic || ""
    : getProfilePic(true) || displayUser?.coverPic || "";
  const rawAvatar = viewingOtherProfile
    ? displayUser?.profilePic || ""
    : getProfilePic() || displayUser?.profilePic || "";

  const coverImage =
    rawCover && !coverFailed ? getImageUrl(rawCover) : coverFallback;
  const avatarImage =
    rawAvatar && !avatarFailed ? getImageUrl(rawAvatar) : avatarFallback;

  const displayName = (() => {
    const fullName = [
      displayUser?.firstname || displayUser?.firstName,
      displayUser?.lastName,
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ");
    const handle = String(
      displayUser?.userName || profileUserName || ""
    ).trim();
    return fullName || handle || "User";
  })();

  const roleLabel =
    displayUser?.userType === "organizer" ||
    displayUser?.userType === "Organizer"
      ? "Organiser"
      : displayUser?.isPromoter
      ? "Promoter"
      : "Member";
  const locationLabel =
    displayUser?.city ||
    displayUser?.location?.city ||
    (!viewingOtherProfile ? userDetails?.city : "") ||
    "";
  const profileSub = locationLabel
    ? `${roleLabel} · ${locationLabel}`
    : roleLabel;

  const profileHref = (() => {
    if (viewingOtherProfile && profileUserName) {
      if (masterToken) {
        return `${process.env.REACT_APP_MERCH_URL}/auth/login/${masterToken}/${profileUserName}`;
      }
      return `${process.env.REACT_APP_SOCIAL_API_URL}/u/${profileUserName}`;
    }
    return `${process.env.REACT_APP_MERCH_URL}/profile`;
  })();

  return (
    <div className="dash-panel event-profile-card" aria-label="User profile">
      <div
        className="event-profile-cover"
        style={{ backgroundImage: `url('${coverImage}')` }}
        aria-hidden="true"
      />
      <div className="event-profile-inner">
        <a
          className="event-profile-row"
          href={profileHref}
          aria-label={displayName}
          onClick={() => onNavigate && onNavigate()}
        >
          <img
            className="event-profile-avatar"
            src={avatarImage}
            alt=""
            onError={() => setAvatarFailed(true)}
          />
          <div className="event-profile-meta">
            <div className="event-profile-name">{displayName}</div>
            <div className="event-profile-sub">
              <i className="bi bi-patch-check-fill" aria-hidden="true"></i>{" "}
              {profileSub}
            </div>
          </div>
        </a>
        <div className="event-profile-stats" aria-label="Profile stats">
          <div className="event-side-stat">
            <span className="event-side-stat-val">{eventsCount}</span>
            <div className="event-side-stat-label">Events</div>
          </div>
          <div className="event-side-stat">
            <span className="event-side-stat-val">{followers || 0}</span>
            <div className="event-side-stat-label">Followers</div>
          </div>
          <div className="event-side-stat">
            <span className="event-side-stat-val">{following || 0}</span>
            <div className="event-side-stat-label">Following</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(EventV2ProfileCard);
